import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"

interface VocalFryConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const PRESET_OPTIONS = [
  {
    key: "strict",
    label: "Strict",
    description: "Catches more possible fry",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Balanced",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags obvious fry",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own thresholds",
  },
]

export const VocalFryConfig: React.FC<VocalFryConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const hasLegacyFields =
    !metricRecordData.metric_metadata?.threshold_preset &&
    metricRecordData.metric_metadata &&
    (metricRecordData.metric_metadata.sample_rate_seconds !== undefined ||
      metricRecordData.metric_metadata.min_fry_segment_seconds !== undefined ||
      metricRecordData.metric_metadata.pitch_floor !== undefined ||
      metricRecordData.metric_metadata.pitch_ceiling !== undefined ||
      metricRecordData.metric_metadata.low_pitch_threshold_multiplier !==
        undefined ||
      metricRecordData.metric_metadata.jitter_threshold_multiplier !==
        undefined ||
      metricRecordData.metric_metadata.hnr_threshold_offset_db !== undefined ||
      metricRecordData.metric_metadata.harmonicity_minimum_pitch !==
        undefined ||
      metricRecordData.metric_metadata.harmonicity_silence_threshold !==
        undefined ||
      metricRecordData.metric_metadata.harmonicity_periods_per_window !==
        undefined ||
      metricRecordData.metric_metadata.baseline_calculation_multiplier !==
        undefined)
  const currentPreset = hasLegacyFields
    ? "custom"
    : (metricRecordData.metric_metadata?.threshold_preset ?? "normal")
  const isCustom = currentPreset === "custom"

  const handlePresetChange = (value: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        threshold_preset: value as "strict" | "normal" | "lenient" | "custom",
      },
    })
  }

  const handleSampleRateSecondsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        sample_rate_seconds: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleMinFrySegmentSecondsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_fry_segment_seconds: isNaN(value) ? undefined : value,
      },
    })
  }

  const handlePitchFloorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        pitch_floor: isNaN(value) ? undefined : value,
      },
    })
  }

  const handlePitchCeilingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        pitch_ceiling: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleLowPitchThresholdMultiplierChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        low_pitch_threshold_multiplier: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleJitterThresholdMultiplierChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        jitter_threshold_multiplier: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleHnrThresholdOffsetDbChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        hnr_threshold_offset_db: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleHarmonicityMinimumPitchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        harmonicity_minimum_pitch: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleHarmonicitySilenceThresholdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        harmonicity_silence_threshold: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleHarmonicityPeriodsPerWindowChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        harmonicity_periods_per_window: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleBaselineCalculationMultiplierChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        baseline_calculation_multiplier: isNaN(value) ? undefined : value,
      },
    })
  }

  const sampleRateSeconds =
    metricRecordData.metric_metadata?.sample_rate_seconds
  const sampleRateSecondsInvalid =
    sampleRateSeconds !== undefined &&
    sampleRateSeconds !== null &&
    sampleRateSeconds < 0

  const minFrySegmentSeconds =
    metricRecordData.metric_metadata?.min_fry_segment_seconds
  const minFrySegmentSecondsInvalid =
    minFrySegmentSeconds !== undefined &&
    minFrySegmentSeconds !== null &&
    minFrySegmentSeconds < 0

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span className={LABEL_STYLES}>Threshold Preset</span>
        </div>
        <Select value={currentPreset} onValueChange={handlePresetChange}>
          <SelectTrigger aria-label="Threshold Preset">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Select a preset or choose Custom to define your own thresholds.
        </p>
      </div>

      {isCustom && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Column 1 */}
          <div className="space-y-4">
            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>Sample Rate (Seconds)</span>
                <Tooltip
                  content="Sample rate for analysis in seconds."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="0.001"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata?.sample_rate_seconds ?? ""
                }
                onChange={handleSampleRateSecondsChange}
                placeholder="0.01"
                aria-label="Sample Rate in Seconds"
              />
              {sampleRateSecondsInvalid && (
                <p className="mt-1 text-sm text-coval-danger-text">
                  Sample Rate must be 0 or greater to save.
                </p>
              )}
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>Min Fry Segment (Seconds)</span>
                <Tooltip
                  content="Minimum vocal fry segment duration to count."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="0.001"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata?.min_fry_segment_seconds ??
                  ""
                }
                onChange={handleMinFrySegmentSecondsChange}
                placeholder="0.05"
                aria-label="Minimum Fry Segment in Seconds"
              />
              {minFrySegmentSecondsInvalid && (
                <p className="mt-1 text-sm text-coval-danger-text">
                  Min Fry Segment must be 0 or greater to save.
                </p>
              )}
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>Pitch Floor (Hz)</span>
                <Tooltip
                  content="Minimum pitch frequency to analyze."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="1"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={metricRecordData.metric_metadata?.pitch_floor ?? ""}
                onChange={handlePitchFloorChange}
                placeholder="75"
                aria-label="Pitch Floor in Hz"
              />
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>Pitch Ceiling (Hz)</span>
                <Tooltip
                  content="Maximum pitch frequency to analyze."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="1"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={metricRecordData.metric_metadata?.pitch_ceiling ?? ""}
                onChange={handlePitchCeilingChange}
                placeholder="600"
                aria-label="Pitch Ceiling in Hz"
              />
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>
                  Low Pitch Threshold Multiplier
                </span>
                <Tooltip
                  content="Multiplier for low pitch threshold calculation."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="0.1"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata
                    ?.low_pitch_threshold_multiplier ?? ""
                }
                onChange={handleLowPitchThresholdMultiplierChange}
                placeholder="1.5"
                aria-label="Low Pitch Threshold Multiplier"
              />
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>
                  Jitter Threshold Multiplier
                </span>
                <Tooltip
                  content="Multiplier for jitter threshold calculation."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="0.1"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata
                    ?.jitter_threshold_multiplier ?? ""
                }
                onChange={handleJitterThresholdMultiplierChange}
                placeholder="2.0"
                aria-label="Jitter Threshold Multiplier"
              />
            </div>
          </div>

          {/* Column 2 */}
          <div className="space-y-4">
            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>HNR Threshold Offset (dB)</span>
                <Tooltip
                  content="Offset for harmonic-to-noise ratio threshold."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="0.1"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata?.hnr_threshold_offset_db ??
                  ""
                }
                onChange={handleHnrThresholdOffsetDbChange}
                placeholder="-10.0"
                aria-label="HNR Threshold Offset in dB"
              />
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>
                  Harmonicity Minimum Pitch (Hz)
                </span>
                <Tooltip
                  content="Minimum pitch for harmonicity calculation."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="1"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata?.harmonicity_minimum_pitch ??
                  ""
                }
                onChange={handleHarmonicityMinimumPitchChange}
                placeholder="75"
                aria-label="Harmonicity Minimum Pitch in Hz"
              />
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>
                  Harmonicity Silence Threshold
                </span>
                <Tooltip
                  content="Threshold below which audio is considered silence."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata
                    ?.harmonicity_silence_threshold ?? ""
                }
                onChange={handleHarmonicitySilenceThresholdChange}
                placeholder="0.1"
                aria-label="Harmonicity Silence Threshold"
              />
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>
                  Harmonicity Periods Per Window
                </span>
                <Tooltip
                  content="Number of periods per analysis window."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="0.1"
                min="1"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata
                    ?.harmonicity_periods_per_window ?? ""
                }
                onChange={handleHarmonicityPeriodsPerWindowChange}
                placeholder="4.5"
                aria-label="Harmonicity Periods Per Window"
              />
            </div>

            <div className={FIELD_CONTAINER_STYLES}>
              <div className={`${LABEL_ROW_STYLES} min-h-10`}>
                <span className={LABEL_STYLES}>
                  Baseline Calculation Multiplier
                </span>
                <Tooltip
                  content="Multiplier for baseline calculation."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className={INFO_ICON_STYLES} />
                  </button>
                </Tooltip>
              </div>
              <input
                type="number"
                step="0.1"
                min="0"
                className={TEXT_INPUT_STYLES}
                value={
                  metricRecordData.metric_metadata
                    ?.baseline_calculation_multiplier ?? ""
                }
                onChange={handleBaselineCalculationMultiplierChange}
                placeholder="1.2"
                aria-label="Baseline Calculation Multiplier"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
